import React from "react";
import { Link } from "react-router-dom";
import { Button } from "primereact/button";
import localeText from "../../../../../../locale";
import { formatPrice } from "../../../../../../utilities/formatPrice";
import {
  EXTRAPAY_CYCLE_PATH,
  EXTRAPAY_KNOW_PATH,
  EXTRAPAY_PATH,
  ORDERS_PER_CYCLE,
} from "../constants";
import {
  getCycleProgressPercent,
  getOrdersProgressLabel,
  getRemainingPurchasesLabel,
} from "../cycleCopy";
import { formatExtrapayRate } from "../formatExtrapay";

const ExtrapayProgramBanner = ({
  isLoading,
  hasError,
  onRetry,
  pointsBalance,
  ordersInCycle,
  cycle,
  conversionRate,
}) => (
  <div
    className="border-round-lg p-4 mb-4"
    style={{
      backgroundColor: "color-mix(in srgb, var(--theme-primary, #1DAF61) 10%, #ffffff)",
      border: "1px solid color-mix(in srgb, var(--theme-primary, #1DAF61) 28%, #ffffff)",
    }}
  >
    <div className="flex flex-column md:flex-row md:align-items-start md:justify-content-between gap-3">
      <div>
        <div className="text-sm mb-3" style={{ color: "var(--theme-primary, #1DAF61)" }}>
          {localeText("extrapayFlowProgram")}
        </div>
        <h3
          className="text-2xl font-bold m-0"
          style={{ color: "var(--theme-primary-dark, #145C45)" }}
        >
          {localeText("extrapayFlowBannerTitle")}
        </h3>
        <p className="text-sm m-0 mt-2" style={{ color: "var(--theme-text, #344563)" }}>
          {localeText("extrapayFlowBannerSubtitle")}
        </p>
      </div>
      <div className="flex flex-column align-items-start md:align-items-end gap-3">
        <Link to={EXTRAPAY_PATH}>
          <Button
            label={localeText("extrapayFlowOpen")}
            icon="pi pi-arrow-right"
            iconPos="right"
            className="border-round-md border-none text-sm font-medium"
            style={{
              backgroundColor: "var(--theme-primary, #1DAF61)",
              color: "#fff",
              padding: "0.625rem",
            }}
          />
        </Link>
        <Link
          to={EXTRAPAY_KNOW_PATH}
          className="text-sm font-medium underline px-1"
          style={{ color: "var(--theme-secondary, #335CFF)" }}
        >
          <i className="pi pi-info-circle mr-2 text-xs font-semibold" />
          {localeText("howItWorks")}
        </Link>
      </div>
    </div>
    <hr style={{ borderColor: "color-mix(in srgb, var(--theme-primary, #1DAF61) 20%, #ffffff)" }} />
    {hasError ? (
      <div className="flex align-items-center gap-3">
        <span className="text-sm" style={{ color: "var(--theme-text)" }}>
          {localeText("extrapayFlowLoadError")}
        </span>
        <Button
          label={localeText("extrapayFlowRetry")}
          className="p-button-text p-0"
          onClick={onRetry}
        />
      </div>
    ) : (
      <div className="grid">
        <div
          className="col-12 md:col-4 p-3"
          style={{
            borderRight: "1px solid color-mix(in srgb, var(--theme-primary, #1DAF61) 20%, #ffffff)",
            ...(window.innerWidth >= 768 ? {} : { borderRight: "none" }),
          }}
        >
          <div className="text-xs mb-1" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
            {localeText("extrapayFlowCyclePoints")}
          </div>
          <div
            className="text-2xl font-bold"
            style={{ color: "var(--theme-primary-dark, #145C45)" }}
          >
            {isLoading ? "—" : formatPrice(pointsBalance)}{" "}
            <span className="text-sm font-medium">{localeText("extrapayFlowPointsUnit")}</span>
          </div>
          <div className="text-xs mt-1" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
            {localeText("extrapayFlowPointsAccumulated")}
          </div>
        </div>
        <div
          className="col-12 md:col-4 p-3"
          style={{
            borderRight: "1px solid color-mix(in srgb, var(--theme-primary, #1DAF61) 20%, #ffffff)",
            ...(window.innerWidth >= 768 ? {} : { borderRight: "none" }),
          }}
        >
          <div className="flex justify-content-between text-xs mb-2">
            <span>{isLoading ? "—" : getOrdersProgressLabel(ordersInCycle)}</span>
            <Link
              to={EXTRAPAY_CYCLE_PATH}
              style={{ color: "#335CFF" }}
              className="text-sm font-medium underline px-1"
            >
              {localeText("extrapayFlowCycleLabel", { cycle: cycle || "—" })}
            </Link>
          </div>
          <div
            className="border-round-xl overflow-hidden"
            style={{ height: "8px", backgroundColor: "#E4E7EC" }}
          >
            <div
              style={{
                width: `${isLoading ? 0 : getCycleProgressPercent(ordersInCycle)}%`,
                height: "100%",
                backgroundColor: "var(--theme-primary, #1DAF61)",
              }}
            />
          </div>
          <div className="text-xs mt-2" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
            {isLoading
              ? localeText("extrapayFlowOrdersProgress", { current: 0, total: ORDERS_PER_CYCLE })
              : getRemainingPurchasesLabel(ordersInCycle)}
          </div>
        </div>
        <div className="col-12 md:col-4 p-3">
          <div className="text-xs mb-1" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
            {localeText("extrapayFlowAccumulatedRate")}
          </div>
          <div
            className="text-2xl font-bold"
            style={{ color: "var(--theme-primary-dark, #145C45)" }}
          >
            {isLoading ? "—" : formatExtrapayRate(conversionRate)}
          </div>
          <div className="text-xs mt-1" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
            {localeText("extrapayFlowRateContinues")}
          </div>
        </div>
      </div>
    )}
  </div>
);

export default ExtrapayProgramBanner;
